import VolumeOffIcon from '@mui/icons-material/VolumeOff'
import VolumeUpIcon from '@mui/icons-material/VolumeUp'
import { Box, IconButton, Stack } from '@mui/material'
import Hls from 'hls.js'
import { usePathname } from 'next/navigation'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { isSafari } from 'react-device-detect'
import type { GetVideoItemCardTrailerQuery } from '~/__generated__/graphql'
import { getLanguageLocalStorage } from '~/features/i18n/utils/languageLocalStorage'
import * as GA from '~/libs/ga'
import { createHls } from '~/libs/hls'
import {
  SUBTITLE_COLOR,
  SUBTITLE_FONT_FAMILY,
  SUBTITLE_FONT_WEIGHT,
  SUBTITLE_LINE_HEIGHT,
  SUBTITLE_TEXT_SHADOW,
  calcSubtitleFontSize,
} from '~/styles/subtitleStyle'

type Trailer = NonNullable<GetVideoItemCardTrailerQuery['originalVideo']>['trailer']

type Props = {
  videoId: string
  trailer?: Trailer | null
}

const HoverTrailerMuteStorageKey = 'hover-trailer-player-mute'

export const HoverTrailerPlayer = ({ videoId, trailer }: Props) => {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const videoRefCb = useCallback((el: HTMLVideoElement | null) => {
    videoRef.current = el
    if (el) setVideoElWidth(el.offsetWidth)
  }, [])
  const asPath = usePathname()

  const screen = useMemo(() => {
    if (asPath === '/') return 'stream'
    if (asPath?.startsWith('/search/categories')) return 'category_videos'
    if (asPath?.startsWith('/search/result')) return 'search_result'
    return undefined
  }, [asPath])

  const src = trailer?.fileUrl

  useEffect(() => {
    if (!src) return
    const video = videoRef.current
    return () => {
      const currentTime = video?.currentTime
      if (screen === undefined || currentTime === undefined || currentTime === 0) return
      GA.sendLog({
        screen,
        action: 'hover_trailer_playback_time',
        value: String(Math.floor(currentTime)),
      })
    }
  }, [screen, videoId, src])

  const [isPlaying, setIsPlaying] = useState(false)

  const [muted, setMuted] = useState(() => {
    if (typeof window === 'undefined') return true
    return window.localStorage.getItem(HoverTrailerMuteStorageKey) !== 'false'
  })

  const [nativeSubtitle, setNativeSubtitle] = useState('')
  const [videoElWidth, setVideoElWidth] = useState(0)

  const vtts = trailer?.vtts ?? []

  const validVtts = useMemo(
    () =>
      (trailer?.vtts ?? []).filter((vtt): vtt is NonNullable<typeof vtts[number]> =>
        Boolean(vtt?.locale),
      ),
    [vtts],
  )
  const defaultVtt = useMemo(() => {
    const setting = getLanguageLocalStorage()
    const bySetting = validVtts.find((vtt) => vtt.locale === setting)
    if (bySetting) return bySetting
    const byDefault = validVtts.find((vtt) => vtt.default)
    if (byDefault) return byDefault
    return validVtts[0]
  }, [validVtts])

  const updateVideoElWidth = useCallback(() => {
    if (videoRef.current) {
      setVideoElWidth(videoRef.current.offsetWidth)
    }
  }, [])

  useEffect(() => {
    updateVideoElWidth()
    window.addEventListener('resize', updateVideoElWidth)
    return () => window.removeEventListener('resize', updateVideoElWidth)
  }, [updateVideoElWidth])

  const handleLoaded = useCallback(() => {
    const video = videoRef.current
    if (!video || !src) return

    updateVideoElWidth()
    video.play()
  }, [updateVideoElWidth])

  const handleChangeMuted = useCallback((muted: boolean) => {
    setMuted(muted)
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(HoverTrailerMuteStorageKey, String(muted))
    }
  }, [muted])

  useEffect(() => {
    const videoElem = videoRef.current
    if (videoElem == null || !src) return

    if (Hls.isSupported() && !isSafari) {
      const hls = createHls({ player: 'hover_trailer', videoId })
      hls.config.xhrSetup = (xhr, url) => {
        xhr.open('GET', url, true)
      }
      hls.detachMedia()
      hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        hls.loadSource(src)
      })
      hls.attachMedia(videoElem)

      return () => {
        hls.destroy()
      }
    }
  }, [src, videoId])

  useEffect(() => {
    const tracks = videoRef.current?.textTracks
    if (!tracks) return

    for (let i = 0; i < tracks.length; i += 1) {
      const track = tracks[i]
      track.mode = 'hidden'
    }
  }, [defaultVtt])

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const updateNativeSubtitle = () => {
      if (!defaultVtt?.locale) {
        setNativeSubtitle('')
        return
      }

      const tracks = video.textTracks
      for (let i = 0; i < tracks.length; i += 1) {
        const track = tracks[i]
        if (track.language === defaultVtt.locale && track.activeCues?.length) {
          const cue = track.activeCues[0] as VTTCue
          setNativeSubtitle((cue?.text ?? '').replace(/\n/g, '<br>'))
          return
        }
      }

      setNativeSubtitle('')
    }

    updateNativeSubtitle()
    video.addEventListener('timeupdate', updateNativeSubtitle)
    video.addEventListener('seeked', updateNativeSubtitle)
    return () => {
      video.removeEventListener('timeupdate', updateNativeSubtitle)
      video.removeEventListener('seeked', updateNativeSubtitle)
    }
  }, [defaultVtt])

  const fontSize = useMemo(
    () => calcSubtitleFontSize(videoElWidth, false),
    [videoElWidth],
  )

  if (!src) {
    return null
  }

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 1,
        overflow: 'hidden',
        borderRadius: 2,
        opacity: isPlaying ? 1 : 0,
        transition: 'opacity 300ms ease',
      }}
    >
      {
        <video
          ref={videoRefCb}
          autoPlay
          playsInline
          loop
          muted={muted}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            pointerEvents: 'none',
          }}
          onLoadedMetadata={handleLoaded}
          onPlay={() => setIsPlaying(true)}
        >
          <source src={src} type='application/vnd.apple.mpegurl' />
          {defaultVtt?.url && (
            <track
              key={`${videoId}-${defaultVtt.locale}`}
              src={`/proxy_resource${new URL(defaultVtt.url).pathname}`}
              kind='captions'
              srcLang={defaultVtt.locale}
              label={defaultVtt.label}
            />
          )}
        </video>
      }
      {nativeSubtitle && (
        <Box
          component='div'
          sx={{
            position: 'absolute',
            width: '100%',
            bottom: '80px',
            display: 'flex',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <span
            style={{
              fontSize: `${fontSize}px`,
              fontFamily: SUBTITLE_FONT_FAMILY,
              fontWeight: SUBTITLE_FONT_WEIGHT,
              textShadow: SUBTITLE_TEXT_SHADOW,
              lineHeight: SUBTITLE_LINE_HEIGHT,
              color: SUBTITLE_COLOR,
              pointerEvents: 'none',
            }}
            dangerouslySetInnerHTML={{ __html: nativeSubtitle }}
          />
        </Box>
      )}
      <Stack
        direction='row'
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          pointerEvents: 'auto',
          zIndex: 2,
        }}
      >
        <IconButton
          size='small'
          onClick={(e) => {
            e.stopPropagation()
            handleChangeMuted(!muted)
          }}
          sx={{
            width: 48,
            height: 48,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' },
          }}
        >
          {muted ? <VolumeOffIcon /> : <VolumeUpIcon />}
        </IconButton>
      </Stack>
    </Box>
  )
}
